'use client'
import { useCallback, useMemo } from 'react'

import { useScrollOffset, useSiteContext } from '../hooks'
import { itemDomId } from './accordion-open-signal'

/**
 * Scroll helpers that land the target below the sticky header (offset from
 * `ScrollOffset`). Two targets:
 * - `scrollToItem(value)` — a single accordion item (by its `value`).
 * - `scrollToContainer(id)` — an arbitrary element id (e.g. the whole group).
 */
export const useAccordionScroll = () => {
  const { scrollOffset } = useScrollOffset()
  const { isMobile } = useSiteContext()

  const scrollToId = useCallback(
    (id: string) => {
      const element = document.getElementById(id)

      if (!element) {
        return
      }

      const topScrollMargin = isMobile ? 8 : 16

      const top =
        element.getBoundingClientRect().top + window.scrollY - scrollOffset.top - topScrollMargin

      window.scrollTo({ top, behavior: 'smooth' })
    },
    [scrollOffset.top, isMobile]
  )

  return useMemo(
    () => ({
      scrollToItem: (value: string) => scrollToId(itemDomId(value)),
      scrollToContainer: (id: string) => scrollToId(id),
    }),
    [scrollToId]
  )
}

export default useAccordionScroll
